import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Form, Button, Container, Alert, Row, Col } from "react-bootstrap";
import axios from "axios";
import { useTranslation } from 'react-i18next';

const LS_DEBT_NAME_HISTORY = "toriDriverDebtNameHistory";
const LS_DEBT_DESC_HISTORY = "toriDriverDebtDescHistory";
const DEBT_FIELD_HISTORY_MAX = 30;

function readStringHistory(key) {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed.filter((x) => typeof x === "string" && x.trim());
    } catch {
        return [];
    }
}

function pushStringHistory(key, value, max) {
    const trimmed = String(value ?? "").trim();
    if (!trimmed) return readStringHistory(key);
    const prev = readStringHistory(key);
    const next = [trimmed, ...prev.filter((x) => x !== trimmed)].slice(0, max);
    try {
        localStorage.setItem(key, JSON.stringify(next));
    } catch (e) {
        console.warn("Could not save debt field history to localStorage", e);
    }
    return next;
}

const DriverDebtDetails = () => {
    const { t } = useTranslation();

    const { id } = useParams();
    const navigate = useNavigate();
    const [debt, setDebt] = useState(null);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [updateSuccess, setUpdateSuccess] = useState(false);
    const [validationError, setValidationError] = useState(null);
    const [nameHistory, setNameHistory] = useState([]);
    const [descHistory, setDescHistory] = useState([]);

    useEffect(() => {
        setNameHistory(readStringHistory(LS_DEBT_NAME_HISTORY));
        setDescHistory(readStringHistory(LS_DEBT_DESC_HISTORY));
    }, []);

    useEffect(() => {
        axios.get("/api/users", { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } })
            .then((res) => setUsers(res.data))
            .catch(() => console.warn("Не вдалося отримати список користувачів"));

        axios.get(`/api/drivers-debts/${id}`)
            .then((res) => {
                setDebt(res.data);
                setLoading(false);
            })
            .catch(() => {
                setError("Не вдалося завантажити дані боргу");
                setLoading(false);
            });
    }, [id]);

    const validateForm = () => {
        if (!debt.debt_total || parseFloat(debt.debt_total) <= 0) {
            setValidationError("Сума боргу повинна бути більше 0");
            return false;
        }
        if (!debt.debt_name.trim()) {
            setValidationError("Назва боргу не може бути порожньою");
            return false;
        }
        if (!debt.debt_create_desc.trim()) {
            setValidationError("Опис боргу не може бути порожнім");
            return false;
        }
        setValidationError(null);
        return true;
    };

    const handleUpdate = () => {
        if (!validateForm()) return;

        axios.put(`/api/drivers-debts/${id}`, debt)
            .then(() => {
                setNameHistory(pushStringHistory(LS_DEBT_NAME_HISTORY, debt.debt_name, DEBT_FIELD_HISTORY_MAX));
                setDescHistory(pushStringHistory(LS_DEBT_DESC_HISTORY, debt.debt_create_desc, DEBT_FIELD_HISTORY_MAX));
                setUpdateSuccess(true);
                setTimeout(() => {
                    setUpdateSuccess(false);
                    navigate("/admin/driver-debts"); // Перехід на сторінку з боргами
                }, 1000);
            })
            .catch(() => alert("Помилка при оновленні"));
    };

    if (loading) return <p>{t("loading")}...</p>;
    if (error) return <Alert variant="danger">{error}</Alert>;
    if (!debt) return <p>{t("noRecords")}</p>;

    const driver = users.find(user => user.id === debt.driver_id);
    const driverName = driver ? `${driver.firstName} ${driver.lastName}` : "Невідомий водій";

    return (
        <Container className="mt-4">
            <h2>{t("debt")} {t("description").toLocaleLowerCase()}</h2>

            {updateSuccess && <Alert variant="success">{t("success")}..</Alert>}
            {validationError && <Alert variant="danger">{validationError}</Alert>}

            <Row className="mb-3">
                <Col md={6}>
                    <strong>{t("driver")}:</strong> {driverName}
                </Col>
                <Col md={6}>
                    <strong>{t("week")}:</strong> {debt.week_number}
                </Col>
            </Row>

            <Row className="mb-3">
                <Col md={6}>
                    <strong>{t("created")}:</strong> {new Date(debt.created_at).toLocaleDateString()}
                </Col>
                <Col md={6}>
                    {debt.debt_status === "closed" && debt.closed_at && (
                        <>
                            <strong>{t("closedAt")}:</strong> {new Date(debt.closed_at).toLocaleDateString()}
                        </>
                    )}
                    {debt.balance && (!debt.closed_at || debt.debt_status !== "closed") && (
                        <>
                            <strong>{t("debt")} {t("value")}:</strong> {debt.balance}
                        </>
                    )}
                </Col>
            </Row>

            <Form>
                <Form.Group>
                    <Form.Label>{t("debt")} {t("value").toLocaleLowerCase()}:</Form.Label>
                    <Form.Control
                        type="number"
                        value={debt.debt_total}
                        onChange={(e) => setDebt({ ...debt, debt_total: e.target.value })}
                    />
                </Form.Group>

                <Form.Group>
                    <Form.Label>{t("debtTitle")}</Form.Label>
                    {!String(debt.debt_name ?? "").trim() && nameHistory.length > 0 && (
                        <>
                            <div className="small text-muted mb-1">{t("debtLocalHistoryHint")}</div>
                            <div className="d-flex flex-wrap gap-1 mb-2">
                                {nameHistory.map((item, idx) => (
                                    <Button
                                        key={`name-hist-${idx}`}
                                        variant="outline-secondary"
                                        size="sm"
                                        className="text-truncate"
                                        style={{ maxWidth: "100%" }}
                                        title={item}
                                        type="button"
                                        onClick={() => setDebt({ ...debt, debt_name: item })}
                                    >
                                        {item.length > 56 ? `${item.slice(0, 56)}…` : item}
                                    </Button>
                                ))}
                            </div>
                        </>
                    )}
                    <Form.Control
                        type="text"
                        list="driver-debt-name-local-history"
                        value={debt.debt_name}
                        onChange={(e) => setDebt({ ...debt, debt_name: e.target.value })}
                    />
                    <datalist id="driver-debt-name-local-history">
                        {nameHistory.map((item, idx) => (
                            <option key={`name-dl-${idx}`} value={item} />
                        ))}
                    </datalist>
                </Form.Group>

                <Form.Group>
                    <Form.Label>{t("debt")} {t("description").toLocaleLowerCase()}</Form.Label>
                    {!String(debt.debt_create_desc ?? "").trim() && descHistory.length > 0 && (
                        <>
                            <div className="small text-muted mb-1">{t("debtLocalHistoryHint")}</div>
                            <div className="d-flex flex-wrap gap-1 mb-2">
                                {descHistory.map((item, idx) => (
                                    <Button
                                        key={`desc-hist-${idx}`}
                                        variant="outline-secondary"
                                        size="sm"
                                        className="text-truncate"
                                        style={{ maxWidth: "100%" }}
                                        title={item}
                                        type="button"
                                        onClick={() => setDebt({ ...debt, debt_create_desc: item })}
                                    >
                                        {item.length > 56 ? `${item.slice(0, 56)}…` : item}
                                    </Button>
                                ))}
                            </div>
                        </>
                    )}
                    <Form.Control
                        as="textarea"
                        value={debt.debt_create_desc}
                        onChange={(e) => setDebt({ ...debt, debt_create_desc: e.target.value })}
                    />
                </Form.Group>

                <Form.Group>
                    <Form.Label>{t("status")}</Form.Label>
                    <Form.Select
                        value={debt.debt_status}
                        onChange={(e) => setDebt({ ...debt, debt_status: e.target.value })}
                    >
                        <option value="open">{t("active")}</option>
                        <option value="closed">{t("inactive")}</option>
                    </Form.Select>
                </Form.Group>

                <Button variant="primary" onClick={handleUpdate} className="mt-3">{t("save")}</Button>
                <Button variant="secondary" className="mt-3 ms-2" onClick={() => navigate(-1)}>{t("back")}</Button>
            </Form>
        </Container>
    );
};

export default DriverDebtDetails;
